import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { InputField } from '../../../components/ui/input-field';
import { supabase } from '../../../integrations/supabase/client';

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
        <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
        <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
);

const Register: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [error, setError] = useState('');
    const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [formData, setFormData] = useState({
        contactName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleGoogleSignUp = async () => {
        setGoogleLoading(true);
        setError('');
        try {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${window.location.origin}/dashboard`,
                },
            });
            if (error) throw error;
        } catch (error: any) {
            console.error('Error con Google:', error);
            setError(error.message || 'Error al registrar con Google');
            setGoogleLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.password || formData.password.length < 6) {
            setError('La contraseña debe tener al menos 6 caracteres');
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Las contraseñas no coinciden');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const { data: authData, error: authError } = await supabase.auth.signUp({
                email: formData.email,
                password: formData.password,
                options: {
                    emailRedirectTo: `${window.location.origin}/dashboard`,
                    data: {
                        nombre_contacto: formData.contactName,
                        telefono: formData.phone,
                    }
                }
            });

            if (authError) throw authError;
            if (!authData.user) throw new Error('Error al crear usuario');

            const userId = authData.user.id;

            const { error: profileError } = await supabase
                .from('profiles')
                .upsert({
                    user_id: userId,
                    nombre_contacto: formData.contactName,
                    telefono: formData.phone,
                }, { onConflict: 'user_id' });

            if (profileError) throw profileError;

            setUserEmail(formData.email);
            setShowEmailConfirmation(true);
        } catch (error: any) {
            console.error('Error en registro:', error);
            setError(error.message || 'Error al registrar usuario');
        } finally {
            setLoading(false);
        }
    };

    if (showEmailConfirmation) {
        return (
            <main className="w-full h-screen flex relative bg-white max-md:flex-col max-md:h-auto">
                <div className="w-6/12 h-full flex flex-col items-center justify-center relative bg-neutral-100 max-md:w-full max-md:min-h-screen max-md:px-5 max-md:py-10 max-sm:p-5">
                    <img
                        src="https://api.builder.io/api/v1/image/assets/TEMP/e4fe78c69a4f9b2dff1a11d2f45f0885adf16022?width=690"
                        alt="Maxi Chatbot Whatsapp AI"
                        className="w-[345px] h-[114px] mb-[60px] max-md:w-[280px] max-md:h-auto max-md:mb-10 max-sm:w-[250px] max-sm:mb-[30px]"
                    />
                    <div className="w-[80%] flex flex-col max-md:w-full max-md:max-sm:max-sm:max-w-xs text-center">
                        <h1 className="text-[#2B3A51] text-center text-lg font-bold mb-[30px] max-sm:text-base max-sm:mb-5">
                            ¡Registro Exitoso!
                        </h1>
                        <div className="bg-green-100 border border-green-400 text-green-700 px-6 py-4 rounded-lg mb-6">
                            <h2 className="text-lg font-semibold mb-2">Revisa tu correo electrónico</h2>
                            <p className="mb-3">
                                Hemos enviado un enlace de confirmación a:
                            </p>
                            <p className="font-semibold text-[#2B3A51] mb-3">{userEmail}</p>
                            <p className="text-sm">
                                Haz clic en el enlace del correo para confirmar tu registro y acceder al dashboard.
                            </p>
                        </div>
                        <div className="text-sm text-gray-600">
                            <p className="mb-2"><strong>Consejos:</strong></p>
                            <ul className="text-left list-disc list-inside space-y-1">
                                <li>Revisa tu carpeta de spam si no ves el correo</li>
                                <li>El enlace es válido por 24 horas</li>
                                <li>Una vez confirmado, podrás acceder directamente</li>
                            </ul>
                        </div>
                        <button
                            type="button"
                            onClick={() => window.location.href = '/login'}
                            className="text-[#2B3A51] text-center text-sm font-normal max-sm:text-[13px] bg-transparent border-none cursor-pointer hover:underline mt-6"
                        >
                            Volver al inicio de sesión
                        </button>
                    </div>
                </div>
                <section
                    className="w-6/12 h-full relative overflow-hidden max-md:w-full max-md:h-[400px] max-sm:h-[300px]"
                    aria-label="Background image section"
                >
                    <img
                        src="https://api.builder.io/api/v1/image/assets/TEMP/906afa4b45dd2fb35efd3b52d64bc69d76718f6a?width=1742"
                        alt="Investment types in Colombia background"
                        className="w-full h-full object-cover"
                    />
                </section>
            </main>
        );
    }

    return (
        <main className="w-full h-screen flex relative bg-white max-md:flex-col max-md:h-auto">
            <div className="w-6/12 h-full flex flex-col items-center justify-center relative bg-neutral-100 max-md:w-full max-md:min-h-screen max-md:px-5 max-md:py-10 max-sm:p-5">
                <img
                    src="https://api.builder.io/api/v1/image/assets/TEMP/e4fe78c69a4f9b2dff1a11d2f45f0885adf16022?width=690"
                    alt="Maxi Chatbot Whatsapp AI"
                    className="w-[345px] h-[114px] mb-[60px] max-md:w-[280px] max-md:h-auto max-md:mb-10 max-sm:w-[250px] max-sm:mb-[30px]"
                />
                <div className="w-[80%] flex flex-col max-md:w-full max-md:max-sm:max-sm:max-w-xs">
                    <h1 className="text-[#2B3A51] text-center text-lg font-bold mb-[30px] max-sm:text-base max-sm:mb-5">
                        Crear cuenta
                    </h1>
                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                            {error}
                        </div>
                    )}

                    {/* Google Sign Up */}
                    <button
                        type="button"
                        onClick={handleGoogleSignUp}
                        disabled={googleLoading}
                        className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-[45px] bg-white hover:bg-gray-50 transition-colors mb-4 disabled:opacity-50"
                    >
                        <GoogleIcon />
                        <span className="text-sm font-medium text-gray-700">
                            {googleLoading ? 'Conectando...' : 'Registrarse con Google'}
                        </span>
                    </button>

                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex-1 h-px bg-gray-300" />
                        <span className="text-xs text-gray-500">o con correo</span>
                        <div className="flex-1 h-px bg-gray-300" />
                    </div>

                    <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3">
                        <InputField
                            label="Nombre"
                            placeholder="Tu nombre completo"
                            name="contactName"
                            value={formData.contactName}
                            onChange={handleInputChange}
                            required
                        />
                        <InputField
                            label="Correo electrónico"
                            placeholder="tucorreo@ejemplo.com"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            type="email"
                        />
                        <InputField
                            label="Teléfono"
                            placeholder="Ej: 573001234567"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            required
                        />
                        <InputField
                            label="Contraseña"
                            placeholder="Mínimo 6 caracteres"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                            type="password"
                        />
                        <InputField
                            label="Confirmar contraseña"
                            placeholder="Repite tu contraseña"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            required
                            type="password"
                        />
                        {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                            <div className="text-red-500 text-sm">
                                Las contraseñas no coinciden
                            </div>
                        )}
                        <Button
                            type="submit"
                            variant="secondary"
                            className="rounded-[45px] px-6 w-full mt-2"
                            disabled={loading}
                        >
                            {loading ? 'Registrando...' : 'Registrarse'}
                        </Button>
                    </form>
                    <button
                        type="button"
                        onClick={() => window.location.href = '/login'}
                        className="text-[#2B3A51] text-center text-sm font-normal max-sm:text-[13px] bg-transparent border-none cursor-pointer hover:underline mt-4"
                    >
                        Ya tengo cuenta - Iniciar Sesión
                    </button>
                </div>
            </div>
            <section
                className="w-6/12 h-full relative overflow-hidden max-md:w-full max-md:h-[400px] max-sm:h-[300px] max-md:hidden"
                aria-label="Background image section"
            >
                <img
                    src="https://api.builder.io/api/v1/image/assets/TEMP/906afa4b45dd2fb35efd3b52d64bc69d76718f6a?width=1742"
                    alt="Investment types in Colombia background"
                    className="w-full h-full object-cover"
                />
            </section>
        </main>
    );
};

export default Register;
